import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';

import { AuthenticationService } from '../../auth/service';
import { switchMap, catchError, map } from 'rxjs/operators';
import { ConfigService } from '../../app-config.service';
import { fetchAuthSession } from 'aws-amplify/auth';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    /**
     *
     * @param {AuthenticationService} _authenticationService
     */
    constructor(
        private _authenticationService: AuthenticationService,
        private configService: ConfigService,
    ) {}

    /**
     * Add auth header with jwt if user is logged in and request is to api url
     * Token is retrieved directly from AWS Amplify sessionStorage (no duplication)
     * @param request
     * @param next
     */
    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const apiUrl = this.configService.getConfig.apiUrl;
        const isApiUrl = request.url.startsWith(apiUrl);
        // Backend revokes the current JWT via blacklist on this route; must send Bearer even if
        // isLoggedIn() is false (e.g. session edge cases) while Amplify still holds the session.
        const isLogoutRequest = isApiUrl && request.url.includes('/auth/logout');

        if (isLogoutRequest) {
            return this.handleWithIdToken(request, next);
        }

        return this._authenticationService
            .isLoggedIn(false)
            .pipe(
                switchMap((loggedIn) =>
                    loggedIn && isApiUrl ? this.handleWithIdToken(request, next) : next.handle(request),
                ),
            );
    }

    /**
     * Sends the request with the ID token Amplify holds (sessionStorage), or without one when the session
     * cannot be read. Only the session read falls back: an error of the request itself reaches the caller
     * untouched. Catching it here re-sent every failed call once, so an import cut by the CloudFront
     * timeout ran a second time.
     */
    private handleWithIdToken(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return from(fetchAuthSession()).pipe(
            map((session) => session.tokens?.idToken?.toString()),
            catchError(() => of(undefined)),
            switchMap((token) =>
                next.handle(token ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : request),
            ),
        );
    }
}
